import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/lib/LanguageContext";
import { MessageCircle, Send, X, Loader2, Sparkles } from "lucide-react";

/**
 * Widget chatbot public — affiché en bas-droite du site tenant si AI Agent activé.
 */
export default function ChatbotWidget() {
  const { t } = useLanguage();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [greeting, setGreeting] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    api
      .get("/ai/chat/widget-config")
      .then((r) => {
        if (r.data?.enabled) {
          setEnabled(true);
          setGreeting(r.data.greeting || "");
        }
      })
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (open && messages.length === 0 && greeting) {
      setMessages([{ role: "assistant", content: greeting }]);
    }
  }, [open, greeting, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat/message", {
        session_id: sessionId,
        message: text,
        context: "patient",
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.response }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: t.chatbot.error },
      ]);
    } finally {
      setSending(false);
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Bouton flottant */}
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={t.chatbot.ariaLabel}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-eh-ink text-eh-bg shadow-2xl flex items-center justify-center hover:scale-110 transition-transform"
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      {/* Fenêtre */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] h-[520px] max-h-[calc(100vh-8rem)] bg-eh-cream rounded-3xl border border-eh-border-soft shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="px-5 py-4 bg-eh-ink text-eh-bg flex items-center gap-3">
            <Sparkles size={18} className="text-amber-300" />
            <div>
              <div className="font-display text-base">{t.chatbot.title}</div>
              <div className="text-xs opacity-60">{t.chatbot.subtitle}</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chatbot-msg-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-eh-ink text-eh-bg rounded-br-sm"
                      : "bg-white border border-eh-border-soft text-eh-ink rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-eh-border-soft px-4 py-2.5 rounded-2xl">
                  <Loader2 className="w-4 h-4 animate-spin text-eh-muted" />
                </div>
              </div>
            )}
          </div>
          <div className="p-3 border-t border-eh-border-soft bg-white flex gap-2">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder={t.chatbot.placeholder}
              disabled={sending}
              className="flex-1 px-4 py-2 rounded-full border border-eh-border-soft text-sm focus:outline-none focus:ring-2 focus:ring-eh-sage-deep/30"
            />
            <button
              data-testid="chatbot-send"
              onClick={send}
              disabled={!input.trim() || sending}
              className="w-10 h-10 rounded-full bg-eh-ink text-eh-bg flex items-center justify-center disabled:opacity-40"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
